/**
 * The shorthand a dispatch desk writes in. It reaches the booking form from two
 * directions — the AI copying a label out of a reservation mail, and an agent
 * typing the way they always have — so the form reads it rather than making
 * either side spell things out.
 *
 * PU/DO, PAX and LUG are also taught to the extractor (`BOOKING_FORMAT` in
 * controller/aiCont.js). These are the client-side half: what the form does
 * with the shorthand once it is holding it.
 */

/** Exactly three capitals — how every IATA airport code is written. */
const IATA = /^[A-Z]{3}$/;

/**
 * Three-capital tokens that appear inside airport labels and are not the code.
 * Short on purpose: extraction only runs against a place dispatch already
 * resolved as an airport, so ordinary street noise ("AVE", "STE") never reaches
 * it and does not need listing.
 */
const NOT_A_CODE = new Set(['FBO', 'USA', 'LLC', 'INC', 'LTD', 'THE', 'AND', 'INT']);

/** True for a bare code an agent typed on its own — "JFK", "LHR", "DXB". */
export const isAirportCode = (text: string): boolean => IATA.test(text.trim());

/**
 * The IATA code carried by any of these strings, first match wins.
 *
 * Google writes it in parentheses ("John F. Kennedy International Airport
 * (JFK)"), which is unambiguous, so that pass runs over every candidate before
 * the bare-token pass — otherwise "EWR Terminal C, Newark (EWR)" could answer
 * from whichever string was passed first rather than from the reliable form.
 */
export function airportCodeIn(...texts: (string | null | undefined)[]): string {
  for (const text of texts) {
    const paren = /\(\s*([A-Z]{3})\s*\)/.exec(text ?? '');
    if (paren) return paren[1];
  }
  for (const text of texts) {
    for (const token of (text ?? '').split(/[^A-Za-z]+/)) {
      if (IATA.test(token) && !NOT_A_CODE.has(token)) return token;
    }
  }
  return '';
}

/**
 * A leg label left glued to the address — "PU: JFK T4", "D/O - 230 Park Ave",
 * "Stop 2 — Grand Central". Searching the label along with the address is what
 * makes the place lookup miss, and it is what the sender's own form template
 * produces when the value is pasted out with its heading.
 *
 * A separator is required, so "Toronto Pearson" keeps its "To" and a vehicle
 * called "Stop and Go" is left alone.
 */
const LEG_LABEL =
  /^\s*(?:p\s?\/?\s?u|pick[\s-]?up|d\s?\/?\s?o|drop[\s-]?off|stop\s*\d*|from|to)\s*(?:location|address|point)?\s*[:\-–—]\s*/i;

export const stripLegLabel = (text: string): string => text.replace(LEG_LABEL, '').trim();

/**
 * "Car Type: Luxury SUV", "Vehicle — Sprinter", "Class: First". The label words
 * are not harmless noise: `fuzzyVehicleMatch` scores shared tokens, so "type"
 * against a fleet listing a "Standard Type" would win on the label alone.
 */
const VEHICLE_LABEL =
  /^\s*(?:(?:car|vehicle|veh)\s*(?:type|class|category)?|type|class|category)\s*[:\-–—]\s*/i;

export const stripVehicleLabel = (text: string): string => text.replace(VEHICLE_LABEL, '').trim();
